import React, { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { observeChartSize } from '../utils/chartResize';
import { sanitizeListingPriceBars } from '../utils/etpChartBars';
import './EtcRollPanel.css';

function fmtPct(value) {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${Number(value).toFixed(2)}%`;
}

function alignRebased(etnBars, benchBars) {
  const benchByTime = new Map(benchBars.map((b) => [b.time, b.close]));
  const aligned = [];
  for (const bar of etnBars) {
    const benchClose = benchByTime.get(bar.time);
    if (benchClose == null) continue;
    aligned.push({ time: bar.time, etn: bar.close, bench: benchClose });
  }
  if (aligned.length < 2) return { etn: [], bench: [], trackingGapPct: null };

  const baseE = aligned[0].etn;
  const baseB = aligned[0].bench;
  const etnSeries = aligned.map((r) => ({
    time: r.time,
    value: (r.etn / baseE) * 100,
  }));
  const benchSeries = aligned.map((r) => ({
    time: r.time,
    value: (r.bench / baseB) * 100,
  }));
  const last = aligned[aligned.length - 1];
  const etnRet = (last.etn / baseE - 1) * 100;
  const benchRet = (last.bench / baseB - 1) * 100;
  return {
    etn: etnSeries,
    bench: benchSeries,
    trackingGapPct: etnRet - benchRet,
    windowDays: aligned.length,
  };
}

function EtnIndexTrackPanel({
  symbol,
  benchmarkSymbol,
  benchmarkLabel,
  className = '',
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const etnSeriesRef = useRef(null);
  const benchSeriesRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trackingGapPct, setTrackingGapPct] = useState(null);
  const [windowDays, setWindowDays] = useState(null);

  useEffect(() => {
    if (!symbol || !benchmarkSymbol) return undefined;

    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const enc = (s) => encodeURIComponent(s);
        const [etnRes, benchRes] = await Promise.all([
          fetch(`/api/investing/prices/${enc(symbol)}?days=400`),
          fetch(`/api/investing/prices/${enc(benchmarkSymbol)}?days=400`),
        ]);
        if (!etnRes.ok || !benchRes.ok) {
          throw new Error(
            !etnRes.ok
              ? `ETN prices HTTP ${etnRes.status}`
              : `Benchmark HTTP ${benchRes.status}`,
          );
        }
        const etnData = await etnRes.json();
        const benchData = await benchRes.json();
        if (cancelled) return;

        const etnBars = sanitizeListingPriceBars(etnData.bars || [], null);
        const benchBars = sanitizeListingPriceBars(benchData.bars || [], null);
        const aligned = alignRebased(etnBars, benchBars);
        if (cancelled) return;

        setTrackingGapPct(aligned.trackingGapPct);
        setWindowDays(aligned.windowDays);

        etnSeriesRef.current?.setData(aligned.etn);
        benchSeriesRef.current?.setData(aligned.bench);
        chartRef.current?.timeScale().fitContent();
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load index tracking');
          setTrackingGapPct(null);
          etnSeriesRef.current?.setData([]);
          benchSeriesRef.current?.setData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [symbol, benchmarkSymbol]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const chart = createChart(el, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      width: el.clientWidth,
      height: 140,
    });

    const etnSeries = chart.addSeries(LineSeries, {
      color: '#A16917',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      title: symbol,
    });
    const benchSeries = chart.addSeries(LineSeries, {
      color: '#1763A1',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      title: benchmarkSymbol,
    });

    chartRef.current = chart;
    etnSeriesRef.current = etnSeries;
    benchSeriesRef.current = benchSeries;

    const disconnect = observeChartSize(el, (width) => {
      chart.applyOptions({ width, height: 140 });
    });

    return () => {
      disconnect();
      chart.remove();
      chartRef.current = null;
      etnSeriesRef.current = null;
      benchSeriesRef.current = null;
    };
  }, [symbol, benchmarkSymbol]);

  const panelClass = `etp-term-structure-dashboard etc-roll-panel etn-index-panel ${className}`.trim();

  return (
    <section className={panelClass} aria-label="Index tracking">
      <h4 className="etc-roll-title">Index tracking</h4>
      <p className="etc-roll-muted etn-index-subtitle">
        {symbol} vs {benchmarkLabel || benchmarkSymbol} (rebased to 100 at start of overlap)
      </p>
      {loading && <p className="etc-roll-muted">Loading…</p>}
      {error && <p className="etc-roll-error">{error}</p>}
      {!loading && !error && trackingGapPct != null && (
        <div className="etc-roll-summary">
          <span className="etc-roll-meta">
            Cumulative gap (ETN − index proxy):
            {' '}
            <strong className="investing-mono">{fmtPct(trackingGapPct)}</strong>
            {windowDays ? ` over ${windowDays} sessions` : ''}
          </span>
        </div>
      )}
      <div ref={containerRef} className="etc-roll-chart" />
      <p className="etc-roll-note">
        ETF proxies track index level, not ETN fees or issuer roll rules. Gap ≈ cost + tracking difference.
      </p>
    </section>
  );
}

export default EtnIndexTrackPanel;
